import { ROLES } from '../types/roles';

/**
 * Complete Filter Helpers
 * Supports both device-based filtering (operational views) and object-based filtering (management interfaces)
 */

// =====================================================
// DEVICE-BASED FILTERING (Existing System)
// Use for operational views where you filter devices
// =====================================================

function getAreaOptions(selectedBuilding, selectedFloorNumber) {
  if (!selectedBuilding) return [];

  return selectedBuilding.areas.filter(area => area.floor.toString() === selectedFloorNumber.toString());
}

function getFloorOptions(selectedBuilding) {
  if (!selectedBuilding) return [];

  const floorsArr = Array.isArray(selectedBuilding.floors)
    ? selectedBuilding.floors
    : Object.values(selectedBuilding.floors || []);

  return floorsArr
    .filter(f => f && f.is_full_floor) // keep only real floors (optional)
    .sort((a, b) => (a.floor ?? 0) - (b.floor ?? 0)) // nice ordering
    .map(f => ({ value: f.id, label: f.floor }));
}

export const getSensorOptions = (filteredDevices = []) => {
  if (!filteredDevices || filteredDevices.length === 0) {
    return [];
  }

  return filteredDevices
    .map(device => ({
      id: device.id,
      name: device.name || `Device ${device.id}`,
      value: device.id,
      floorNumber: device.floor_number,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const matchesDeviceSearch = (device, searchString) => {
  const search = String(searchString || '').toLowerCase();
  const name = String(device?.name || '').toLowerCase();
  const mac = String(device?.mac || '').toLowerCase();
  return name.includes(search) || mac.includes(search);
};

/**
 * Filter devices based on selected criteria
 * @param {Array} devices - Array of device objects
 * @param {Object} filters - Filter criteria {building, floor, area, status, searchString}
 * @returns {Array} Filtered devices
 */
export const filterDevices = (devices = [], filters = {}, currUser) => {
  const { distributor, customer, building, floor, area, status, searchString, hvacEquipment } = filters;
  const isAll = value => value === 'all' || value === '' || value === null || value === undefined;
  const hasMembership = (list, target) =>
    Array.isArray(list) && list.some(item => String(item) === String(target));
  const matchesSearch = device => matchesDeviceSearch(device, searchString);

  if (currUser.role === 's') {
    return devices
      .filter(matchesSearch)
      .filter(d => (isAll(distributor) ? true : hasMembership(d.dist_ids, distributor)))
      .filter(d => (isAll(customer) ? true : hasMembership(d.cust_ids, customer)))
      .filter(d => (isAll(building) ? true : d.building_id?.toString() === String(building)))
      .filter(d => (isAll(floor) ? true : d.floor_number?.toString() === String(floor)))
      .filter(d => (isAll(area) ? true : d.area_id?.toString() === String(area)))
      .filter(d => (isAll(hvacEquipment) ? true : hasMembership(d.hvac_equipment_ids, hvacEquipment)))
      .filter(d =>
        isAll(status) ? true : status === 'active' ? d.status?.toString() !== '4' : d.status?.toString() === String(status),
      )
      .sort((a, b) => a.floor_number - b.floor_number);
  } else if (currUser.role === 'd') {
    return devices
      .filter(matchesSearch)
      .filter(d => (isAll(customer) ? true : hasMembership(d.cust_ids, customer)))
      .filter(d => (isAll(building) ? true : d.building_id?.toString() === String(building)))
      .filter(d => (isAll(floor) ? true : d.floor_number?.toString() === String(floor)))
      .filter(d => (isAll(area) ? true : d.area_id?.toString() === String(area)))
      .filter(d => (isAll(hvacEquipment) ? true : hasMembership(d.hvac_equipment_ids, hvacEquipment)))
      .filter(d =>
        isAll(status) ? true : status === 'active' ? d.status?.toString() !== '4' : d.status?.toString() === String(status),
      )
      .sort((a, b) => a.floor_number - b.floor_number);
  } else {
    return devices
      ?.filter(matchesSearch)
      .filter(d => (isAll(building) ? true : d.building_id?.toString() === String(building)))
      .filter(d => (isAll(floor) ? true : d.floor_number?.toString() === String(floor)))
      .filter(d => (isAll(area) ? true : d.area_id?.toString() === String(area)))
      .filter(d => (isAll(hvacEquipment) ? true : hasMembership(d.hvac_equipment_ids, hvacEquipment)))
      .filter(d =>
        isAll(status) ? true : status === 'active' ? d.status?.toString() !== '4' : d.status?.toString() === String(status),
      )
      .sort((a, b) => a.floor_number - b.floor_number);
  }

  // return devices.filter(device => {
  //     // Customer filter
  //     // if (customer !== '' && customer !== 'all' && device.cust_ids.find(custId => custId === customer)) {
  //     //   return true;
  //     // }
  //     if (customer === 'all' || device.cust_ids.find(custId => custId === customer)) {
  //       return true;
  //     }

  //     // Building filter
  //     if (building === 'all' || device.building_id?.toString() === building) {
  //       return true;
  //     }

  //     // Floor filter
  //     if (floor !== 'all' && device.floor_number?.toString() !== floor) {
  //       return false;
  //     }

  //     // Area filter
  //     if (area !== 'all' && device.area_id.toString() !== area) {
  //       return false;
  //     }

  //     // Status filter
  //     if (status !== 'all' && device.status?.toString() !== status) {
  //       return false;
  //     }

  //     // Search filter (search in name and id)
  //     if (searchString && searchString.trim()) {
  //       const search = searchString.toLowerCase();
  //       const nameMatch = device.name?.toLowerCase().includes(search);
  //       const idMatch = device.id?.toString().toLowerCase().includes(search);
  //       const macMatch = device.mac?.toLowerCase().includes(search);

  //       if (!nameMatch && !idMatch && !macMatch) {
  //         return false;
  //       }
  //     }

  //     return false;
  //   })
  //   .sort((a, b) => {
  //     // Sort by building, then floor, then area, then name
  //     const buildingCompare = (a.building || '').localeCompare(b.building || '');
  //     if (buildingCompare !== 0) return buildingCompare;

  //     const floorCompare = (a.floor_number || 0) - (b.floor_number || 0);
  //     if (floorCompare !== 0) return floorCompare;

  //     const areaCompare = (a.area_name || '').localeCompare(b.area_name || '');
  //     if (areaCompare !== 0) return areaCompare;

  //     return (a.name || '').localeCompare(b.name || '');
  //   });
};

// =====================================================
// MANAGEMENT-BASED FILTERING (New System)
// Use for management interfaces (user/building management)
// =====================================================

/**
 * Generate management interface filter options from users and buildings objects
 * Use this for user/building management interfaces instead of generateFilterOptions
 * @param {Array} users - Array of user objects from state.users.users
 * @param {Array} buildings - Array of building objects from state.sensors.buildings
 * @param {Object} currentFilters - Current filter selections
 * @returns {Object} Filter options for dropdowns
 */
export const generateManagementFilterOptions = (users = [], buildings = [], currentFilters = {}) => {
  if (!users || !buildings) {
    return {
      buildings: [], //[{ value: 'all', label: 'All Buildings' }],
      customers: [], //[{ value: 'all', label: 'All Customers' }],
      distributors: [], //[{ value: 'all', label: 'All Distributors' }],
      users: [{ value: 'all', label: 'All Users' }],
      roles: getRoleOptions(),
      areas: [{ value: 'all', label: 'All Areas' }],
      floors: [{ value: 'all', label: 'All Floors' }],
    };
  }

  const { role, building: selectedBuildingId, floor: selectedFloorNumber } = currentFilters;

  // Filter data based on current selections for cascading dropdowns
  let filteredBuildings = buildings;
  let availableAreas = [];
  let availableFloors = [];
  let availableUsers = users;

  // Filter users by role if selected
  if (role && role !== 'all') {
    availableUsers = users.filter(user => user.role === role);
  }

  // If a building is selected, get its areas and floors
  if (selectedBuildingId && selectedBuildingId !== 'all') {
    const selectedBuilding = buildings.find(b => b.id.toString() === selectedBuildingId);
    if (selectedBuilding) {
      availableFloors = Object.values(selectedBuilding.floors || {});
      availableAreas = Object.values(selectedBuilding.areas || {});
      availableAreas = availableAreas.filter(a => Number(a.floor) === Number(selectedFloorNumber))

      // Also filter users by building access
      availableUsers = availableUsers.filter(user =>
        user.building_permissions?.some(perm => perm.building_id === selectedBuilding.id),
      );
    }
  } else {
    // If no building selected, get all areas and floors from all buildings
    buildings.forEach(building => {
      availableAreas.push(...Object.values(building.areas || {}));
      availableFloors.push(...Object.values(building.floors || {}));
    });
  }

  return {
    buildings: getBuildingOptionsFromBuildings(buildings),
    customers: getCustomerOptionsFromUsers(availableUsers),
    distributors: getDistributorOptionsFromUsers(availableUsers),
    users: getUserOptionsFromUsers(availableUsers),
    roles: getRoleOptions(),
    areas: getAreaOptionsFromAreas(availableAreas),
    floors: getFloorOptionsFromFloors(availableFloors),
  };
};

/**
 * Generate building options from buildings array
 */
function getBuildingOptionsFromBuildings(buildings) {
  return [
    //{ value: 'all', label: 'All Buildings' },
    ...[...buildings]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(building => ({
        value: building.id.toString(),
        label: building.name,
        devices_count: building.devices_count,
        city: building.city,
        street: building.street,
      })),
  ];
}

/**
 * Generate user options (for user management interfaces)
 */
const getUserOptionsFromUsers = users => {
  return [
    { value: 'all', label: 'All Users' },
    ...[...users]
      .sort((a, b) => {
        // Sort by role priority (s > d > c), then by name
        const rolePriority = { s: 3, d: 2, c: 1 };
        const roleCompare = (rolePriority[b.role] || 0) - (rolePriority[a.role] || 0);
        if (roleCompare !== 0) return roleCompare;
        return a.full_name.localeCompare(b.full_name);
      })
      .map(user => ({
        value: user.id.toString(),
        label: `${user.full_name} (${user.role_display})`,
        role: user.role,
        building_count: user.building_permissions?.length || 0,
        username: user.username,
      })),
  ];
};

/**
 * Generate customer options from users array
 */
const getCustomerOptionsFromUsers = users => {
  const customers = users.filter(user => user.role === 'c');

  return [
    //{ value: 'all', label: 'All Customers' },
    ...[...customers]
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map(user => ({
        value: user.id.toString(),
        label: `${user.full_name} (${user.username})`,
        building_count: user.building_permissions?.length || 0,
        username: user.username,
      })),
  ];
};

/**
 * Generate distributor options from users array
 */
const getDistributorOptionsFromUsers = users => {
  const distributors = users.filter(user => user.role === 'd');

  return [
    //{ value: 'all', label: 'All Distributors' },
    ...[...distributors]
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map(user => ({
        value: user.id.toString(),
        label: `${user.full_name} (${user.username})`,
        customer_count: user.customer_count || 0,
        building_count: user.building_permissions?.length || 0,
        username: user.username,
      })),
  ];
};

/**
 * Generate role options
 */
const getRoleOptions = () => [
  { value: 'all', label: 'All Roles' },
  ...Object.values(ROLES).map(roleObj => ({
    value: roleObj.role,
    label: roleObj.role_display,
  })),
];

/**
 * Generate area options from areas array
 */
const getAreaOptionsFromAreas = areas => {
  // Remove duplicates by area name and id
  const uniqueAreas = Array.from(new Map(areas.map(area => [area.id, area])).values());

  return [
    { value: 'all', label: 'All Areas' },
    ...[...uniqueAreas]
      .sort((a, b) => {
        // Sort by floor first, then by name
        if (a.floor !== b.floor) {
          return a.floor - b.floor;
        }
        return a.name.localeCompare(b.name);
      })
      .map(area => ({
        value: area.id.toString(),
        label: area.name,
        floor: area.floor,
        building_id: area.building_id,
        id: area.id,
      })),
  ];
};

/**
 * Generate floor options from floors array
 */
const getFloorOptionsFromFloors = floors => {
  // Remove duplicates and sort by floor number
  const uniqueFloors = Array.from(new Map(floors.map(floor => [floor.floor, floor])).values());

  return [
    { value: 'all', label: 'All Floors' },
    ...[...uniqueFloors]
      .sort((a, b) => a.floor - b.floor)
      .map(floor => ({
        value: floor.floor.toString(),
        label: `Floor ${floor.floor}`,
        area_size: floor.area_size,
        building_id: floor.building_id,
        id: floor.id,
      })),
  ];
};

/**
 * Filter users based on selected criteria
 * @param {Array} users - Array of user objects
 * @param {Object} filters - Filter criteria {role, building, searchString}
 * @returns {Array} Filtered users
 */
export const filterUsers = (users = [], filters = {}) => {
  const { role, building, searchString } = filters;

  return users
    .filter(user => {
      // Role filter
      if (role && role !== 'all' && user.role !== role) {
        return false;
      }

      // Building filter - check if user has permission for this building
      if (building && building !== 'all') {
        const hasBuilding = user.building_permissions?.some(perm => perm.building_id.toString() === building);
        if (!hasBuilding) {
          return false;
        }
      }

      // Search filter (search in name, username, email)
      if (searchString && searchString.trim()) {
        const search = searchString.toLowerCase();
        const nameMatch = user.full_name?.toLowerCase().includes(search);
        const usernameMatch = user.username?.toLowerCase().includes(search);
        const emailMatch = user.email?.toLowerCase().includes(search);

        if (!nameMatch && !usernameMatch && !emailMatch) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by role priority (s > d > c), then by name
      const rolePriority = { s: 3, d: 2, c: 1 };
      const roleCompare = (rolePriority[b.role] || 0) - (rolePriority[a.role] || 0);
      if (roleCompare !== 0) return roleCompare;

      return a.full_name.localeCompare(b.full_name);
    });
};

/**
 * Filter buildings based on selected criteria
 * @param {Array} buildings - Array of building objects
 * @param {Object} filters - Filter criteria {user, searchString}
 * @returns {Array} Filtered buildings
 */
export const filterBuildings = (buildings = [], filters = {}) => {
  const { user, searchString } = filters;

  return buildings
    .filter(building => {
      // User filter - check if building has this user
      if (user && user !== 'all') {
        const hasUser = building.users && building.users[user];
        if (!hasUser) {
          return false;
        }
      }

      // Search filter (search in name, city, street)
      if (searchString && searchString.trim()) {
        const search = searchString.toLowerCase();
        const nameMatch = building.name?.toLowerCase().includes(search);
        const cityMatch = building.city?.toLowerCase().includes(search);
        const streetMatch = building.street?.toLowerCase().includes(search);

        if (!nameMatch && !cityMatch && !streetMatch) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Get users by building ID
 */
export const getUsersByBuilding = (buildings, buildingId) => {
  const building = buildings.find(b => b.id.toString() === buildingId);
  return building ? Object.values(building.users || {}) : [];
};

/**
 * Get buildings by user ID
 */
export const getBuildingsByUser = (users, userId) => {
  const user = users.find(u => u.id.toString() === userId);
  return user ? user.building_permissions || [] : [];
};

/**
 * Convert new dropdown format to old format for existing components
 * Used during transition period
 */
export const convertToLegacyFormat = newOptions => {
  return newOptions.map(opt => ({
    value: opt.value,
    name: opt.label,
    id: opt.id,
  }));
};

/**
 * Generate filter options for FilterBar - combines user permissions with device data
 * @param {Array} users - User array from state
 * @param {Array} devices - Device array from state
 * @param {Object} currentUser - Current user object
 * @param {Object} currentFilters - Current filter selections
 * @returns {Object} Filter options for dropdowns
 */

/**
 * Generate filter options for FilterBar - combines user permissions with device data
 * @param {Array} users - User array from state
 * @param {Array} devices - Device array from state
 * @param {Object} currentUser - Current user object
 * @param {Object} currentFilters - Current filter selections
 * @param {Array} buildings - Buildings array from state
 * @returns {Object} Filter options for dropdowns
 */
// export const generateFilterBarOptions = (
//   users = [],
//   currentUser = null,
//   currentFilters = {},
//   buildings = [],
//   includeAllOptions = true,
// ) => {
//   if (!users || users.length === 0) {
//     return {
//       distributors: [],
//       customers: [],
//       buildings: [],
//       floors: [{ value: 'all', label: 'All' }],
//       areas: []
//     };
//   }

//   // const { distributor, customer, building, floor } = currentFilters;
//   const selectedDistributor = currentFilters.distributor;
//   const selectedCustomer = currentFilters.customer;
//   const selectedBuildingId = currentFilters.building;
//   const selectedFloorNumber = currentFilters.floor;
//   const selectedUserId =
//     selectedDistributor !== '' && selectedDistributor !== 'all' ? selectedDistributor : selectedCustomer;

//   // STEP 1: Distributors (only for super admin)
//   const distributorUsers = users.filter(user => user.role === 'd');
//   const distributorsOptions = [
//     ...[{ value: 'all', label: 'All Distributors' }], // CONDITIONAL
//     ...distributorUsers.map(user => ({
//       value: user.id.toString(),
//       label: user.username,
//     })),
//   ];

//   // STEP 2: Customers - role-based logic
//   let availableCustomers = [];
//   if (currentUser?.role === 's') {
//     availableCustomers = [
//       ...users
//         .filter(user => user.role === 'c')
//         .map(user => ({
//           value: user.id.toString(),
//           label: user.username,
//         })),
//     ];
//   } else if (currentUser?.role === 'd') {
//     const apiCustomers = users?.filter(u => u.role === 'c') || [];
//     availableCustomers = [
//       ...apiCustomers.map(customer => ({
//         value: customer.id?.toString() || customer.value,
//         label: customer.label || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.username,
//       })),
//     ];
//   }

//   // Get permitted buildings based on role
//   let buildingsOptions = [];

//   // Helper to normalize any building object -> { value, label }
//   const toOption = b => ({
//     value: b.id ?? b.building_id,
//     label: b.name ?? b.building_name ?? `Building ${b.id ?? b.building_id}`,
//   });

//   if (currentUser?.role === 'c') {
//     // Customer: only their buildings
//     if (Array.isArray(currentUser?.building_permissions)) {
//       buildingsOptions = currentUser.building_permissions.map(toOption);
//     } else if (currentUser?.building_ids) {
//       const buildingIds = Array.isArray(currentUser.building_ids)
//         ? currentUser.building_ids
//         : [currentUser.building_ids];

//       // Try to resolve names from `buildings` if available; fall back to "Building X"
//       const byId = new Map((buildings ?? []).map(b => [String(b.id ?? b.building_id), b]));
//       buildingsOptions = buildingIds.map(id => {
//         const b = byId.get(String(id));
//         return toOption(b ?? { id, name: undefined });
//       });
//     } else {
//       buildingsOptions = [];
//     }
//   } else {
//     // Super admin / Distributor
//     if (String(selectedUserId) === 'all') {
//       // All buildings from the global `buildings` variable
//       buildingsOptions = (buildings ?? []).map(toOption);
//     } else {
//       // Buildings of the selected user
//       const selectedUser = users.find(u => String(u.id) === String(selectedUserId));
//       buildingsOptions = (selectedUser?.building_permissions ?? []).map(toOption);
//     }
//   }

//   // (optional) de-dupe & sort by label
//   buildingsOptions = Array.from(new Map(buildingsOptions.map(o => [String(o.value), o])).values()).sort((a, b) =>
//     a.label.localeCompare(b.label),
//   );

//   const selectedBuilding = buildings?.find(b => String(b.id) === String(selectedBuildingId));

//   const uniqueFloorsOptions = getFloorOptions(selectedBuilding);

//   const floorOptions = [
//     { value: 'all', label: 'All' },
//     ...uniqueFloorsOptions.map(floor => ({
//       value: floor.label.toString(),
//       label: `Floor ${floor.label}`,
//     })),
//   ];

//   const uniqueAreasOptions = getAreaOptions(selectedBuilding, selectedFloorNumber);

//   const areaOptions = [
//     { value: 'all', label: 'All' },
//     ...uniqueAreasOptions.map(area => ({
//       value: String(area.id),
//       label: area.name,
//     })),
//   ];

//   const result = {
//     distributors: distributorsOptions,
//     customers: [...[{ value: 'all', label: 'All Customers' }], ...availableCustomers],
//     buildings: [...[{ value: 'all', label: 'All Buildings' }], ...buildingsOptions],
//     floors: floorOptions,
//     areas: areaOptions,
//   };

//   return result;
// };


export const generateFilterBarOptions = (
  users = [],
  currentUser = null,
  currentFilters = {},
  buildings = [],
  includeAllOptions = true,
) => {
  if (!Array.isArray(users) || users.length === 0) {
    return {
      distributors: [],
      customers: [],
      buildings: [],
      floors: [{ value: 'all', label: 'All' }],
      areas: [],
    };
  }

  const selectedDistributor = currentFilters.distributor;
  const selectedCustomer = currentFilters.customer;
  const selectedBuildingId = currentFilters.building ?? 'all';
  const selectedFloorNumber = currentFilters.floor ?? 'all';

  const selectedUserId =
    selectedDistributor && selectedDistributor !== 'all' && selectedDistributor !== ''
      ? selectedDistributor
      : selectedCustomer;

  // ---------- helpers ----------
  const getBuildingById = (buildingsArr, id) => {
    const sid = String(id);
    return (buildingsArr || []).find(
      b => String(b?.id ?? b?.building_id ?? b?.value) === sid
    ) || null;
  };

  const toBuildingOption = b => ({
    value: String(b?.id ?? b?.building_id ?? b?.value),
    label: b?.name ?? b?.building_name ?? b?.label ?? `Building ${b?.id ?? b?.building_id ?? b?.value ?? ''}`.trim(),
  });

  const uniqueByValue = arr =>
    Array.from(new Map(arr.filter(Boolean).map(o => [String(o.value), o])).values());

  const sortByLabel = arr => arr.slice().sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  const normFloors = (bld) => {
    // try common shapes
    const raw = bld?.floors ?? bld?.floor_ids ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map(f => {
      if (typeof f === 'object') {
        return {
          id: String(f.id ?? f.value ?? f.floor_id ?? ''),
          num: Number(f.floor ?? f.number ?? f.label ?? f.num),
        };
      }
      // primitive: assume it's the floor number (no id known)
      return { id: null, num: Number(f) };
    }).filter(x => !Number.isNaN(x.num));
  };

  const normAreas = (bld) => {
    const raw = bld?.areas ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map(a => ({
      id: String(a?.id ?? a?.value ?? ''),
      name: a?.name ?? a?.label ?? `Area ${a?.id ?? a?.value ?? ''}`,
      floorNum: Number(a?.floor ?? a?.floor_num ?? a?.level),
    }));
  };

  /**
   * NEW: Resolve floors from bp.floor_and_area_ids using the 'buildings' arg.
   * - If an id matches a floor.id  → use that floor's number
   * - Else if it matches an area.id → use that area's floor number
   * - De-dupe & sort ascending
   */
  const extractFloors = (bp, buildingsArr) => {
    const floorNums = new Set();
    const bld = getBuildingById(buildingsArr, bp?.building_id);

    if (!bld) return [];

    const floors = normFloors(bld);                   // [{id, num}]
    const areas  = normAreas(bld);                    // [{id, name, floorNum}]
    const floorIdToNum = new Map(floors.filter(f => f.id).map(f => [String(f.id), f.num]));
    const areaIdToFloor = new Map(areas.filter(a => a.id).map(a => [String(a.id), a.floorNum]));

    const ids = Array.isArray(bp?.floor_and_area_ids) ? bp.floor_and_area_ids : [];

    ids.forEach(rawId => {
      const id = String(rawId);
      if (floorIdToNum.has(id)) {
        floorNums.add(floorIdToNum.get(id));
      } else if (areaIdToFloor.has(id)) {
        const n = areaIdToFloor.get(id);
        if (!Number.isNaN(n)) floorNums.add(n);
      }
    });

    return Array.from(floorNums).filter(n => !Number.isNaN(n)).sort((a, b) => a - b);
  };

  /**
   * NEW: Resolve areas from bp.floor_and_area_ids for a specific floor using the 'buildings' arg.
   * Only returns areas whose id appears in floor_and_area_ids (or all if floor='all').
   */
  const extractAreasForFloor = (bp, floorNumber, buildingsArr) => {
    const bld = getBuildingById(buildingsArr, bp?.building_id);
    if (!bld) return [];

    const ids = new Set(
      (Array.isArray(bp?.floor_and_area_ids) ? bp.floor_and_area_ids : []).map(x => String(x))
    );
    const allAreas = normAreas(bld); // every area in the building

    // If floor='all' → any area listed in floor_and_area_ids
    if (String(floorNumber) === 'all') {
      return allAreas
        .filter(a => ids.has(a.id))
        .map(a => ({ value: a.id, label: a.name }));
    }

    const fnum = Number(floorNumber);
    if (Number.isNaN(fnum)) return [];

    // Specific floor → only areas from that floor that are listed in floor_and_area_ids
    return allAreas
      .filter(a => !Number.isNaN(a.floorNum) && a.floorNum === fnum && ids.has(a.id))
      .map(a => ({ value: a.id, label: a.name }));
  };

  // ---------- build distributors/customers (unchanged) ----------
  const distributorUsers = users.filter(u => u.role === 'd');
  const distributorsOptions = [
    { value: 'all', label: 'All Distributors' },
    ...distributorUsers.map(u => ({ value: String(u.id), label: u.username })),
  ];

  let availableCustomers = [];
  if (currentUser?.role === 's') {
    availableCustomers = users.filter(u => u.role === 'c').map(u => ({ value: String(u.id), label: u.username }));
  } else if (currentUser?.role === 'd') {
    const apiCustomers = users?.filter(u => u.role === 'c') || [];
    availableCustomers = apiCustomers.map(c => ({
      value: c.id?.toString() || c.value,
      label: c.label || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.username,
    }));
  }

  // ---------- main logic: when a distributor/customer is selected, build from his building_permissions ----------
  let buildingsOptions = [];
  let floorOptions = [{ value: 'all', label: 'All' }];
  let areaOptions  = [{ value: 'all', label: 'All' }];

  const selectedUser =
    selectedUserId && selectedUserId !== 'all'
      ? users.find(u => String(u.id) === String(selectedUserId))
      : null;

  if (selectedUser && Array.isArray(selectedUser.building_permissions) && selectedUser.building_permissions.length) {
    // Buildings from that user
    buildingsOptions = sortByLabel(
      uniqueByValue(
        selectedUser.building_permissions.map(bp => toBuildingOption({
          id: bp?.building_id,
          building_id: bp?.building_id,
          building_name: bp?.building_name,
          name: bp?.building_name,
        }))
      )
    );

    // Floors from that user's permissions (via buildings data)
    const floorCollector = new Set();
    if (selectedBuildingId && selectedBuildingId !== 'all') {
      const bp = selectedUser.building_permissions.find(
        b => String(b?.building_id) === String(selectedBuildingId)
      );
      if (bp) extractFloors(bp, buildings).forEach(n => floorCollector.add(n));
    } else {
      selectedUser.building_permissions.forEach(bp => {
        extractFloors(bp, buildings).forEach(n => floorCollector.add(n));
      });
    }

    const floorsArr = Array.from(floorCollector).sort((a, b) => a - b).map(n => ({
      value: String(n),
      label: `Floor ${n}`,
    }));
    floorOptions = [{ value: 'all', label: 'All' }, ...floorsArr];

    // Areas from that user's permissions (resolved by buildings)
    let areasArr = [];
    if (selectedBuildingId && selectedBuildingId !== 'all') {
      const bp = selectedUser.building_permissions.find(
        b => String(b?.building_id) === String(selectedBuildingId)
      );
      if (bp) {
        if (String(selectedFloorNumber) === 'all') {
          // collect all areas listed in floor_and_area_ids for this building
          areasArr = extractAreasForFloor(bp, 'all', buildings);
        } else {
          areasArr = extractAreasForFloor(bp, selectedFloorNumber, buildings);
        }
      }
    } else {
      // No specific building selected:
      if (String(selectedFloorNumber) === 'all') {
        selectedUser.building_permissions.forEach(bp => {
          areasArr = areasArr.concat(extractAreasForFloor(bp, 'all', buildings));
        });
      } else {
        // We cannot reliably filter by a specific floor across multiple buildings → leave 'All'
        areasArr = [];
      }
    }

    areaOptions = [{ value: 'all', label: 'All' }, ...sortByLabel(uniqueByValue(areasArr))];
  } else {
    // ---------- fallback (legacy/global) ----------
    if (currentUser?.role === 'c') {
      if (Array.isArray(currentUser?.building_permissions)) {
        buildingsOptions = currentUser.building_permissions.map(toBuildingOption);
      } else if (currentUser?.building_ids) {
        const buildingIds = Array.isArray(currentUser.building_ids) ? currentUser.building_ids : [currentUser.building_ids];
        const byId = new Map((buildings ?? []).map(b => [String(b.id ?? b.building_id), b]));
        buildingsOptions = buildingIds.map(id => toBuildingOption(byId.get(String(id)) ?? { id }));
      }
    } else {
      if (String(selectedUserId) === 'all') {
        buildingsOptions = (buildings ?? []).map(toBuildingOption);
      } else {
        const su = users.find(u => String(u.id) === String(selectedUserId));
        buildingsOptions = (su?.building_permissions ?? []).map(bp =>
          toBuildingOption({ id: bp?.building_id, building_id: bp?.building_id, name: bp?.building_name })
        );
      }
    }

    buildingsOptions = sortByLabel(uniqueByValue(buildingsOptions));

    // Floors from selected building (global)
    const selectedBuilding = getBuildingById(buildings, selectedBuildingId);
    const floorsList = normFloors(selectedBuilding || {});
    floorOptions = [
      { value: 'all', label: 'All' },
      ...Array.from(new Set(floorsList.map(f => f.num)))
        .sort((a, b) => a - b)
        .map(n => ({ value: String(n), label: `Floor ${n}` })),
    ];

    // Areas from selected building (global)
    const areasList = normAreas(selectedBuilding || {});
    const fnum = Number(selectedFloorNumber);
    const filteredAreas =
      String(selectedFloorNumber) === 'all'
        ? areasList
        : areasList.filter(a => a.floorNum === fnum);
    areaOptions = [
      { value: 'all', label: 'All' },
      ...filteredAreas.map(a => ({ value: a.id, label: a.name })),
    ];
  }

  return {
    distributors: distributorsOptions,
    customers: [{ value: 'all', label: 'All Customers' }, ...availableCustomers],
    buildings: [{ value: 'all', label: 'All Buildings' }, ...buildingsOptions],
    floors: floorOptions,
    areas: areaOptions,
  };
};
